/**
 * Purchase Order Print Utility
 * Professional black & white A4 format
 */

export const generatePurchaseOrderHTML = (po, settings = {}) => {
  const companyName = settings.companyName || 'The Perfume Stories (Pvt) Ltd.';
  const companyAddress = settings.companyAddress || '';
  const companyEmail = settings.companyEmail || '';
  const companyPhone = settings.companyPhone || '';
  const companyLogo = settings.companyLogo || '';

  const fmt = (n) =>
    parseFloat(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const orderDate = po.orderDate?.toDate
    ? po.orderDate.toDate().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : po.orderDate || '—';

  const deliveryDate = po.expectedDeliveryDate?.toDate
    ? po.expectedDeliveryDate.toDate().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
    : po.expectedDeliveryDate || '—';

  const addressLines = companyAddress
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => `<div>${l}</div>`)
    .join('');

  const itemRows = (po.items || []).map((item, idx) => `
    <tr>
      <td class="center">${idx + 1}</td>
      <td>${item.productName || item.name || ''}</td>
      <td>${item.category || '—'}</td>
      <td class="center">${item.quantity}</td>
      <td class="right">${fmt(item.costPrice)}</td>
      <td class="right bold">${fmt(item.subtotal || item.quantity * item.costPrice)}</td>
    </tr>
  `).join('');

  const total = po.totalAmount || (po.items || []).reduce((s, i) => s + (i.subtotal || i.quantity * i.costPrice), 0);

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Purchase Order ${po.poNumber}</title>
  <style>
    @page { size: A4; margin: 16mm; }
    @media print {
      @page { margin: 16mm; }
      body { margin: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      font-size: 12px;
      color: #000;
      background: #fff;
      line-height: 1.5;
    }

    /* ── HEADER ── */
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding-bottom: 16px;
      border-bottom: 2px solid #000;
      margin-bottom: 20px;
    }
    .logo-area {
      display: flex;
      align-items: center;
      gap: 14px;
    }
    .logo-img {
      width: 80px;
      height: 80px;
      object-fit: contain;
    }
    .company-info .name {
      font-size: 15px;
      font-weight: 700;
      margin-bottom: 3px;
    }
    .company-info .detail {
      font-size: 11px;
      color: #333;
      line-height: 1.6;
    }
    .po-title-block {
      text-align: right;
    }
    .po-title {
      font-size: 28px;
      font-weight: 700;
      letter-spacing: 3px;
      text-transform: uppercase;
      margin-bottom: 6px;
    }
    .po-number {
      font-size: 13px;
      font-weight: 700;
      margin-bottom: 4px;
    }
    .po-date {
      font-size: 11px;
      color: #444;
    }

    /* ── META ROW ── */
    .meta-row {
      display: flex;
      justify-content: space-between;
      margin-bottom: 24px;
      gap: 20px;
    }
    .meta-box {
      flex: 1;
      border: 1px solid #ccc;
      padding: 12px 14px;
      border-radius: 2px;
    }
    .meta-box .label {
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #555;
      margin-bottom: 6px;
      border-bottom: 1px solid #eee;
      padding-bottom: 4px;
    }
    .meta-box .value {
      font-size: 13px;
      font-weight: 600;
    }
    .meta-box .sub {
      font-size: 11px;
      color: #444;
      margin-top: 2px;
    }

    /* ── ITEMS TABLE ── */
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 0;
    }
    thead tr {
      background: #1a1a1a;
      color: #fff;
    }
    thead th {
      padding: 9px 10px;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.3px;
      border: none;
    }
    tbody tr {
      border-bottom: 1px solid #e8e8e8;
    }
    tbody tr:nth-child(even) {
      background: #f9f9f9;
    }
    tbody td {
      padding: 9px 10px;
      font-size: 12px;
      border: none;
    }
    .center { text-align: center; }
    .right  { text-align: right; }
    .bold   { font-weight: 700; }

    /* ── TOTALS ── */
    .totals-wrapper {
      display: flex;
      justify-content: flex-end;
      border-top: 2px solid #000;
      padding-top: 10px;
      margin-top: 0;
    }
    .totals-table {
      width: 280px;
      font-size: 12px;
    }
    .totals-table td {
      padding: 5px 10px;
    }
    .totals-table .grand-total td {
      font-size: 14px;
      font-weight: 700;
      border-top: 1px solid #000;
      padding-top: 8px;
    }

    /* ── FOOTER ── */
    .footer {
      margin-top: 32px;
      display: flex;
      justify-content: space-between;
      gap: 20px;
    }
    .footer-box {
      flex: 1;
    }
    .footer-box .section-label {
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: #555;
      margin-bottom: 6px;
    }
    .footer-box .content {
      font-size: 11px;
      color: #333;
      line-height: 1.6;
    }
    .sig-line {
      margin-top: 40px;
      border-top: 1px solid #000;
      padding-top: 6px;
      font-size: 11px;
      color: #555;
      width: 200px;
    }
    .doc-footer {
      margin-top: 24px;
      padding-top: 10px;
      border-top: 1px solid #ccc;
      display: flex;
      justify-content: space-between;
      font-size: 10px;
      color: #888;
    }
  </style>
</head>
<body>

  <!-- HEADER -->
  <div class="header">
    <div class="logo-area">
      ${companyLogo
        ? `<img src="${companyLogo}" alt="Logo" class="logo-img" />`
        : ''}
      <div class="company-info">
        <div class="name">${companyName}</div>
        <div class="detail">
          ${addressLines}
          ${companyPhone ? `<div>${companyPhone}</div>` : ''}
          ${companyEmail ? `<div>${companyEmail}</div>` : ''}
        </div>
      </div>
    </div>
    <div class="po-title-block">
      <div class="po-title">Purchase Order</div>
      <div class="po-number"># ${po.poNumber}</div>
      <div class="po-date">Date: ${orderDate}</div>
      ${deliveryDate !== '—' ? `<div class="po-date">Expected Delivery: ${deliveryDate}</div>` : ''}
    </div>
  </div>

  <!-- META: Supplier + Delivery -->
  <div class="meta-row">
    <div class="meta-box">
      <div class="label">Supplier / Vendor</div>
      <div class="value">${po.supplierName || '—'}</div>
      ${po.supplierPhone ? `<div class="sub">${po.supplierPhone}</div>` : ''}
      ${po.supplierEmail ? `<div class="sub">${po.supplierEmail}</div>` : ''}
    </div>
    <div class="meta-box">
      <div class="label">Ship To</div>
      <div class="value">${companyName}</div>
      <div class="sub">${companyAddress.split('\n')[0] || ''}</div>
    </div>
    <div class="meta-box" style="max-width:160px;">
      <div class="label">Status</div>
      <div class="value" style="text-transform:capitalize;">${po.status || 'Pending'}</div>
      <div class="sub">Currency: LKR</div>
    </div>
  </div>

  <!-- ITEMS TABLE -->
  <table>
    <thead>
      <tr>
        <th class="center" style="width:4%">#</th>
        <th style="width:38%">Description</th>
        <th style="width:18%">Category</th>
        <th class="center" style="width:10%">Qty</th>
        <th class="right" style="width:15%">Unit Price (LKR)</th>
        <th class="right" style="width:15%">Total (LKR)</th>
      </tr>
    </thead>
    <tbody>
      ${itemRows}
    </tbody>
  </table>

  <!-- TOTALS -->
  <div class="totals-wrapper">
    <table class="totals-table">
      <tr>
        <td>Sub Total</td>
        <td class="right">${fmt(total)}</td>
      </tr>
      <tr class="grand-total">
        <td>Grand Total</td>
        <td class="right">LKR ${fmt(total)}</td>
      </tr>
    </table>
  </div>

  <!-- FOOTER -->
  <div class="footer">
    ${po.notes ? `
    <div class="footer-box">
      <div class="section-label">Notes / Special Instructions</div>
      <div class="content">${po.notes}</div>
    </div>` : '<div class="footer-box"></div>'}

    ${po.requireSignature ? `
    <div class="footer-box" style="text-align:right; max-width:220px;">
      <div class="sig-line" style="margin-left:auto; margin-top:48px;">Authorized Signature</div>
    </div>` : ''}
  </div>

  <!-- DOC FOOTER -->
  <div class="doc-footer">
    <span>${companyName}</span>
    <span>This is a computer generated document.</span>
    <span>${po.poNumber}</span>
  </div>

</body>
</html>`;
};

/** Open browser print dialog */
export const printPurchaseOrder = async (po, settings = {}) => {
  const html = generatePurchaseOrderHTML(po, settings);
  const win = window.open('', '_blank', 'width=900,height=700');
  if (!win) throw new Error('Please allow popups to print purchase orders');
  win.document.write(html);
  win.document.close();
  win.onload = () => {
    const imgs = win.document.images;
    if (imgs.length === 0) { setTimeout(() => win.print(), 300); return; }
    let loaded = 0;
    const tryPrint = () => { if (++loaded === imgs.length) setTimeout(() => win.print(), 300); };
    Array.from(imgs).forEach((img) => {
      if (img.complete) tryPrint();
      else { img.onload = tryPrint; img.onerror = tryPrint; }
    });
  };
};
